/**
 * AuthGuard – wraps the entire app and redirects unauthenticated users to LoginPage.
 * Also handles the post-redirect callback from Azure AD.
 */
import React, { useEffect, useState } from 'react';
import {
  useIsAuthenticated,
  useMsal,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import LoginPage from '../components/Login/LoginPage';
import { McLoadingIndicator } from '@maersk-global/mds-react-wrapper/components-core/mc-loading-indicator';
import { McNotification } from '@maersk-global/mds-react-wrapper/components-core/mc-notification';
import { McButton } from '@maersk-global/mds-react-wrapper/components-core/mc-button';
import { COLORS } from '../constants/theme';
import { SESSION_EXPIRED_EVENT, signInAgain } from './tokenManager';

function LoadingSpinner() {
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: COLORS.background,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <McLoadingIndicator label="Signing you in…" variant="ring" fit="large" orientation="vertical" />
    </div>
  );
}

// Shown when the token can't be renewed, so users know why data stopped loading.
function SessionExpiredBanner() {
  return (
    <div role="alert" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000 }}>
      <McNotification
        appearance="warning"
        icon="exclamation-triangle"
        heading="Your session has expired."
        body="Redirecting you to sign in…"
        actionsposition="right"
        verticalalign="middle"
        disableroundedcorners
        width="100%"
      >
        <span slot="actions" className="mc-notification__actions">
          <McButton
            label="Sign in again"
            appearance="neutral"
            variant="outlined"
            fit="small"
            click={signInAgain}
          />
        </span>
      </McNotification>
    </div>
  );
}

const AuthGuard = ({ children }) => {
  const { inProgress, instance } = useMsal();
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    const onExpired = () => setSessionExpired(true);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  // Handle the redirect response when the page loads after Azure AD redirect
  useEffect(() => {
    instance.handleRedirectPromise().catch((err) => {
      console.error('[AuthGuard] Redirect error:', err);
    });
  }, [instance]);

  // While MSAL is processing a redirect/login, show a spinner
  if (inProgress !== InteractionStatus.None) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <AuthenticatedTemplate>
        {sessionExpired && <SessionExpiredBanner />}
        {children}
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
    </>
  );
};

export default AuthGuard;

